import React, { Component, useContext } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import { Link } from "react-router-dom"
import { Label } from "reactstrap"
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'

import logo from '../assets/images/logo.png'
import { ReactComponent as AccidentIcon } from '../assets/images/icons/accident.svg'
import { HasPermissionsQuery, UserQuery } from "../Functions/querys"
import withApolloClient from '../withApolloClient'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import LockedIndicator from '../Material/LockedIndicator'
import { isPermitted, NO_PERMISSION_MESSAGE } from '../Functions/permissions'
import { pricingPromotionTabs } from '../PriceManagement/PricingPromotionsPage'
import { tpaTabs } from '../ThirdPartyAdministrator/TPAPage'
import { maintenanceTabs } from '../Maintenance/MaintenancePage'
import { organizationTabs } from '../Organization/OrganizationPage'
import { ROUTES } from './routes'
import { isOnRoute } from '../Functions/pathTabs'

// Merged nav items must be gated on every permission their destination page serves, not
// just the first tab's - otherwise a user holding only a later tab's permission gets
// locked out of a feature they're authorized for. Pulling the codes straight off each
// page's own tab config (rather than hand-copying strings here) means the two can't drift:
// add/remove/re-gate a tab in the page component and the sidebar's permission set follows.
// .filter(Boolean) removes ungated tabs (permissionRequired: null, as ClaimsPage and
// ReservationsPage's tabs already use), guarding against nulls in the GraphQL `[String]!`
// variable and handling the all-ungated case (where the array would be empty, and the item
// falls through to permitted). On a mixed page (some tabs gated, some not), this approach
// still denies users whose only access is the ungated tab; such a page would need
// "any of the gated codes OR any ungated tab exists" logic.
const tabPermissionCodes = (tabs) => tabs.map(tab => tab.permissionRequired).filter(Boolean)

// A sidebar item's `link` is a route's base path, so a non-default tab (living one segment
// below it, e.g. /incidents/assigned) no longer matches it exactly. Built once from ROUTES
// so a route lookup by path doesn't need a hand-maintained table alongside MENU_ITEMS.
const ROUTES_BY_PATH = new Map(Object.values(ROUTES).map(route => [route.path, route]))

const InboxComponent = () => {
    const { currentUser } = useContext(CurrentUserContext)
    const count = currentUser?.assignedUnreadConversationsCount ?? 0
    if (count === 0) return null

    const capped = count > 99 ? '99+' : count
    return <span className="sidebar-badge" aria-live="polite">
        {capped}
    </span>
}

const MENU_ITEMS = [
    {
        parentName: "Tasks",
        items: [
            { name: "Boards", link: "/boards/tasks/", icon: "fa-columns", isVisible: true, permissionRequired: "view_taskrabbit" },
        ],
    },
    {
        parentName: "Communication",
        items: [
            { name: "Inbox", link: "/comms/", icon: "fa-inbox", isVisible: true, permissionRequired: "view_conversation", extraComponent: <InboxComponent /> },
            { name: "Settings", link: "/comms/admin/", icon: "fa-cog", isVisible: true, permissionRequired: "change_inbox" },
        ],
    },
    {
        parentName: "Onboarding",
        items: [
            { name: "Leads", link: ROUTES.leads.path, icon: "fa-user-plus", isVisible: true, permissionRequired: null },
            { name: "Applications", link: ROUTES.applications.path, icon: "fa-file-text-o", isVisible: true, permissionRequired: null },
            { name: "Insurances", link: ROUTES.insurances.path, icon: "fa-shield", isVisible: true, permissionRequired: null },
            { name: "Reservations", link: ROUTES.reservations.path, icon: "fa-calendar-o", isVisible: true, permissionRequired: null },
        ],
    },
    {
        parentName: "Drivers",
        items: [
            { name: "Drivers", link: ROUTES.drivers.path, icon: "fa-user", isVisible: true, permissionRequired: null },
            { name: "Let's Follow Up", link: ROUTES.driversFollowUp.path, icon: "fa-bell-o", isVisible: true, permissionRequired: null },
            { name: "Interactions", link: ROUTES.driversInteractions.path, icon: "fa-exchange", isVisible: true, permissionRequired: null },
            { name: "Argyle", link: ROUTES.argyle.path, icon: "fa fa-plug", isVisible: true, permissionRequired: null },
        ],
    },
    {
        parentName: "Billing",
        items: [
            { name: "Collections", link: ROUTES.collections.path, icon: "fa-usd", isVisible: true, permissionRequired: null },
            { name: "New Collections", link: ROUTES.collectionsSpine.path, icon: "fa-bullseye", isVisible: true, permissionRequired: "view_collectioncase" },
            { name: "Scheduled Payments", link: ROUTES.scheduledPayments.path, icon: "fa-clock-o", isVisible: true, permissionRequired: null },
            { name: "Payment Plans", link: ROUTES.paymentPlans.path, icon: "fa-calendar-o", isVisible: true, permissionRequired: null },
            { name: "Subscriptions", link: ROUTES.subscriptions.path, icon: "fa-refresh", isVisible: true, permissionRequired: null },
        ],
    },
    {
        parentName: "Cars",
        items: [
            { name: "Available Cars", link: ROUTES.availableCars.path, icon: "fa-car", isVisible: true, permissionRequired: null },
            { name: "Fleet Management", link: ROUTES.fleet.path, icon: "fa-files-o", isVisible: true, permissionRequired: null },
            { name: "Pricing & Promotions", link: ROUTES.pricing.path, icon: "fa-money", isVisible: true, permissionRequiredAny: tabPermissionCodes(pricingPromotionTabs) },
            { name: "Maintenance", link: ROUTES.maintenance.path, icon: "fa-wrench", isVisible: true, permissionRequiredAny: tabPermissionCodes(maintenanceTabs) },
            { name: "Car Inspections", link: ROUTES.carInspections.path, icon: "fa-clipboard", isVisible: true, permissionRequired: null },
            { name: "Registrations", link: ROUTES.registrations.path, icon: "fa-registered", isVisible: false, permissionRequired: "view_carregistration" },
            { name: "Inspections", link: ROUTES.dmvInspections.path, icon: "fa-file-text-o", isVisible: false, permissionRequired: "view_carinspection" },
        ],
    },
    {
        parentName: "Rentals",
        items: [
            { name: "Rentals", link: ROUTES.rentals.path, icon: "fa-road", isVisible: true, permissionRequired: null },
            { name: "Car Returns", link: ROUTES.carReturns.path, icon: "fa-reply", isVisible: true, permissionRequired: null },
        ],
    },
    {
        parentName: "Incidents",
        items: [
            { name: "Incidents", link: ROUTES.incidents.path, customIcon: <AccidentIcon width={20} height={20} fill={"#ffff"} />, isVisible: true, permissionRequired: null },
            { name: "Claims", link: ROUTES.claims.path, icon: "fa fa-balance-scale", isVisible: true, permissionRequired: null },
            { name: "TPA", link: ROUTES.tpa.path, icon: "fa-users", isVisible: true, permissionRequiredAny: tabPermissionCodes(tpaTabs) },
        ],
    },
    {
        parentName: "More",
        items: [
            { name: "Organization", link: ROUTES.organization.path, icon: "fa-sitemap", isVisible: true, permissionRequiredAny: tabPermissionCodes(organizationTabs) },
            { name: "BOS Attributes", link: ROUTES.attributes.path, icon: "fa-database", isVisible: true, permissionRequired: null },
            { name: "Report Builder", link: "/report_builder/", icon: "fa-table", isVisible: true, permissionRequired: null, external: true },
            { name: "Dashboard", link: "/dashboard/", icon: "fa-bar-chart", isVisible: true, permissionRequired: null },
            { name: "Uploads", link: "/uploads/", icon: "fa-upload", isVisible: true, permissionRequired: null },
            { name: "Automations", link: ROUTES.automations.path, icon: "fa-cogs", isVisible: true, permissionRequired: "view_automation" },
            { name: "Chatbot", link: "/chatbot/", icon: "fa-cogs", isVisible: true, permissionRequired: "view_chatbot_custom" },
            { name: "Notification Templates", link: "/notification_templates/", icon: "fa-cogs", isVisible: true, permissionRequired: null },
        ],
    },
]

export { MENU_ITEMS }

const UpdateMember = gql`
    mutation updateMember($input: UpdateMemberMutationInput!){
        updateMember(input:$input){
        ok
        errors{
            messages
            field
        }
    }
}`

export class SideBar extends Component {
    constructor(props) {
        super(props)
        this.state = {
            showPinnedMenuItems: false,
            preferredMenuItems: [],
        }
        this.sidebarTooltipRef = React.createRef()
    }

    getMenuItemInitials = (name) => {
        if (!name) return ""

        return name
            .split(/[\s/-]+/)
            .filter(Boolean)
            .slice(0, 3)
            .map((item) => item.charAt(0).toUpperCase())
            .join("")
    }

    hasItemPinned = (menuItem) => {
        return menuItem.items
            .filter(subItem => subItem.isVisible)
            .filter(subItem => this.state.preferredMenuItems && this.state.preferredMenuItems.includes(subItem.name))
            .length > 0
    }

    renderMenuItem = (subItem, key, pathname) => {
        // this.props.loading is HasPermissionsQuery's own loading flag (distinct from the
        // currentUserLoading skeleton above, which races a different query and can resolve
        // first). isPermitted fails closed on an unresolved hasPermissions, which is correct
        // once we genuinely know the answer is "no" - but while the query is still in
        // flight we don't know that yet, so treat gated items as permitted rather than
        // flashing a false "you don't have permission" lock and swallowing clicks on every
        // cold load. Worst case is a brief moment where a truly-unauthorized item looks
        // clickable; some destinations (the merged tabbed pages, and anything wrapped in
        // RestrictedSection) do re-check client-side once their own query resolves, but
        // several others (Boards, Inbox, Settings, New Collections, Registrations,
        // Inspections, Chatbot) have no client-side permission check at all. That's not a
        // new hole this introduces, though: those destinations were already reachable by URL
        // before this change, since hiding a nav item was never itself enforcement - the
        // real gate is, and always was, server-side.
        const permitted = this.props.loading ? true : isPermitted(subItem, this.props.hasPermissions)
        const isPinned = this.state.preferredMenuItems && this.state.preferredMenuItems.includes(subItem.name)
        // A tabbed listing page's sidebar item stays active on its non-default tabs too
        // (/incidents/assigned, /cars/maintenance/work-orders, ...), not just at its own
        // base path. Items with no matching route (Tasks/Comms/More) keep the exact match.
        const route = ROUTES_BY_PATH.get(subItem.link)
        const active = route ? isOnRoute(route, pathname) : pathname === subItem.link
        const icon = subItem.customIcon
            ? subItem.customIcon
            : <i className={`fa fa-lg ${subItem.icon ? subItem.icon : "fa-info-circle"}`} aria-hidden="true" />

        // The icon gets a shrink-wrapping wrapper so the collapsed rail's lock badge can
        // anchor to the icon's own box (top:0; right:0) instead of a fixed offset from the
        // row's centre - Font Awesome glyph widths vary too much (~9.7px-18.8px measured
        // across current items) for a magic-number offset to track every icon's corner.
        // The wrapper renders for every item, locked or not, so unlocked rows keep
        // identical markup/layout; it only ever contains the icon itself for them.
        // A second LockedIndicator (a11y-decorative, matching the existing inline one)
        // renders inside the wrapper for locked items only, purely for the collapsed
        // corner badge - see index-dark.css's .sidebar-menu-item-corner-badge /
        // .sidebar-menu-item-inline-badge rules for which one is visible in which state.
        // Two instances rather than one reordered via flex `order` because the corner
        // badge needs true DOM containment inside the icon wrapper to size/position off
        // it, while the expanded row needs the lock to stay after the label text.
        const body = (
            <>
                <span className="sidebar-menu-item-icon">
                    {icon}
                    {!permitted && <LockedIndicator decorative className="sidebar-menu-item-corner-badge" />}
                </span>
                <span className="list-item-text">{subItem.name}</span>
                {subItem.extraComponent}
            </>
        )

        return (
            <li
                key={key}
                className={[
                    active ? "active" : "inactive",
                    "sidebar-menu-item",
                    permitted ? "" : "bos-locked",
                ].filter(Boolean).join(" ")}
                onMouseEnter={(event) => this.showSidebarTooltip(event, permitted ? subItem.name : `${subItem.name} — ${NO_PERMISSION_MESSAGE}`)}
                onMouseLeave={this.hideSidebarTooltip}
            >
                {permitted ?
                    <Link to={subItem.link} target={subItem.external ? "_blank" : null} aria-label={subItem.name}>
                        {body}
                    </Link> :
                    <span
                        className="sidebar-menu-item-locked"
                        aria-label={`${subItem.name} — ${NO_PERMISSION_MESSAGE}`}
                        aria-disabled="true"
                        tabIndex={0}
                        onFocus={(event) => this.showSidebarTooltip(event, `${subItem.name} — ${NO_PERMISSION_MESSAGE}`)}
                        onBlur={this.hideSidebarTooltip}
                    >
                        {body}
                        <LockedIndicator decorative className="sidebar-menu-item-inline-badge" />
                    </span>
                }
                <i
                    className={`fa fa-thumb-tack menu-list-item-pin ${isPinned ? "active-pin" : ""}`}
                    onClick={this.addRemovePinnedItem(subItem.name)}
                    aria-hidden="true"
                />
            </li>
        )
    }

    addRemovePinnedItem = (itemName) => {
        return () => {
            let preferredMenuItems = [...this.state.preferredMenuItems]
            if (preferredMenuItems.includes(itemName)) {
                preferredMenuItems = preferredMenuItems.filter(item => item !== itemName)
            } else {
                preferredMenuItems.push(itemName)
            }
            this.setState({ preferredMenuItems })
        }
    }

    showSidebarTooltip = (event, text) => {
        const tooltip = this.sidebarTooltipRef.current
        if (!tooltip || !text) return

        const rect = event.currentTarget.getBoundingClientRect()
        tooltip.textContent = text
        tooltip.style.top = `${rect.top + (rect.height / 2)}px`
        tooltip.style.left = `${rect.right + 12}px`
        tooltip.classList.add('is-visible')
    }

    hideSidebarTooltip = () => {
        const tooltip = this.sidebarTooltipRef.current
        if (!tooltip) return
        tooltip.classList.remove('is-visible')
    }

    componentDidUpdate(prevProps, prevState) {
        if (!(prevProps.currentUser && prevProps.currentUser.member) && this.props.currentUser && this.props.currentUser.member && this.props.currentUser.member.showPinnedMenuItems !== this.state.showPinnedMenuItems) {
            this.setState({ showPinnedMenuItems: this.props.currentUser.member.showPinnedMenuItems })
        }
        if (!(prevProps.currentUser && prevProps.currentUser.member) && this.props.currentUser && this.props.currentUser.member && this.props.currentUser.member.preferredMenuItems !== this.state.preferredMenuItems) {
            this.setState({ preferredMenuItems: this.props.currentUser.member.preferredMenuItems })
        }
        if (prevState.showPinnedMenuItems !== this.state.showPinnedMenuItems && this.props.currentUser && this.props.currentUser.member && this.props.currentUser.member.showPinnedMenuItems !== this.state.showPinnedMenuItems) {
            this.updateSelectedInfo(this.props.currentUser.member.id, 'showPinnedMenuItems', this.state.showPinnedMenuItems)
        }
        if (prevState.preferredMenuItems !== this.state.preferredMenuItems && this.props.currentUser && this.props.currentUser.member && this.props.currentUser.member.preferredMenuItems !== this.state.preferredMenuItems) {
            this.updateSelectedInfo(this.props.currentUser.member.id, 'preferredMenuItems', this.state.preferredMenuItems)
        }
    }

    updateSelectedInfo = (memberId, name, value) => {
        this.setState({ loading: true })
        let input = { memberId }

        if (name === 'preferredMenuItems') {
            input.preferredMenuItems = value
        } else if (name === 'showPinnedMenuItems') {
            input.showPinnedMenuItems = value
        }

        this.props.apolloClient.mutate({
            mutation: UpdateMember,
            variables: { input },
        }).then((result) => {
            this.setState({ loading: false })

            if (result.data && result.data.updateMember && result.data.updateMember.ok) {
                this.props.refetchUserQuery()
                this.setState({ loading: false, keyVersion: this.state.keyVersion + 1 })
            } else {
                this.setState({ loading: false })
            }
        })
    };

    render() {
        const pathname = window.location.pathname
        const isCollapsed = !this.props.showLeftNav
        return (
            <div className={"bos-left-nav" + (this.props.showLeftNav ? " show" : " hide")}>
                <ul>
                    <li className="bos-logo-item">
                        <img src={logo} width={isCollapsed ? "35px" : "75%"} alt="BOS Logo" />
                    </li>
                    {this.props.currentUserLoading ?
                        <SkeletonTheme baseColor="#202020" highlightColor="#444">
                            <Skeleton count={30} height={30} />
                        </SkeletonTheme> :
                        this.state.showPinnedMenuItems ?
                            <>
                                <Label className="pinned-item-label">Pinned <i className="fa fa-thumb-tack pinned-item-label-icon" aria-hidden="true" /></Label>
                                {MENU_ITEMS.filter(menuItem => this.hasItemPinned(menuItem)).length > 0 ? MENU_ITEMS.filter(menuItem => this.hasItemPinned(menuItem)).map((menuItem, index) =>
                                    <React.Fragment key={index}>
                                        <Label>{menuItem.parentName}</Label>
                                        {menuItem.items
                                            .filter(subItem => subItem.isVisible)
                                            .filter(subItem => this.state.preferredMenuItems.includes(subItem.name))
                                            .map((subItem, subIndex) => this.renderMenuItem(subItem, subIndex, pathname))}
                                    </React.Fragment>
                                ) : <div className="text-center text-danger pt-3">No Items Found!</div>}
                            </> :
                            MENU_ITEMS.map((menuItem, index) =>
                                <React.Fragment key={index}>
                                    <Label>{menuItem.parentName}</Label>
                                    {menuItem.items
                                        .filter(subItem => subItem.isVisible)
                                        .map((subItem, subIndex) => this.renderMenuItem(subItem, subIndex, pathname))}
                                </React.Fragment>
                            )
                    }
                </ul>
                <div className="bos-left-nav-footer">
                    <a className="sidebar-footer-action sidebar-footer-action-pin" onClick={() => this.setState({ showPinnedMenuItems: !this.state.showPinnedMenuItems })} title={this.state.showPinnedMenuItems ? "Show all menu items" : "Show pinned menu items"}>
                        <i className={this.state.showPinnedMenuItems ? "fa fa-lg fa-bars" : "fa fa-lg fa-thumb-tack"} aria-hidden="true" />
                        <span className="sidebar-footer-action-text">{this.state.showPinnedMenuItems ? "All" : "Pinned"}</span>
                    </a>

                    <a className="sidebar-footer-action sidebar-footer-action-toggle" onClick={this.props.updateShowLeftNav} title={isCollapsed ? "Expand" : "Collapse"}>
                        <i className={`fa fa-lg ${isCollapsed ? "fa-angle-double-right" : "fa-angle-double-left"}`} aria-hidden="true" />
                        <span className="sidebar-footer-action-text">{isCollapsed ? "Expand" : "Collapse"}</span>
                    </a>
                </div>
                <div ref={this.sidebarTooltipRef} className="bos-sidebar-tooltip" />

            </div>
        )
    }
}


// Derived, not hand-maintained: walking MENU_ITEMS for every permissionRequired /
// permissionRequiredAny code it actually references means this list can't go stale the
// way a hand-copied one did (finding: it kept requesting codes for nav items that had
// since been deleted or merged away) and can't silently miss a code a new item needs
// (which would permanently lock that item for everyone).
const ALL_MENU_PERMISSION_CODES = Array.from(new Set(
    MENU_ITEMS
        .flatMap(group => group.items)
        .flatMap(item => item.permissionRequiredAny || (item.permissionRequired ? [item.permissionRequired] : []))
))

//-----------------------------------
export default graphql(HasPermissionsQuery, {
    options: () => ({
        variables: {
            userPermissions: ALL_MENU_PERMISSION_CODES,
        },
    }),
    props: ({ data: { hasPermissions, loading, variables } }) => ({
        hasPermissions,
        loading,
        variables
    })
})(
    graphql(UserQuery, {
        props: ({ data: { currentUser, loading, refetch } }) => ({
            currentUser,
            currentUserLoading: loading,
            refetchUserQuery: () => {
                return refetch({
                    query: UserQuery,
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        return { currentUser: fetchMoreResult.currentUser }
                    }
                })
            }
        })
    })(withApolloClient(SideBar))
)
